import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Row,
  Space,
  Spin,
  Typography,
} from "antd";
import { has } from "lodash-es";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import { useResetForm } from "@/hooks";
import { useHallDepositSchema, THallDepositSchema } from "@/schemas";
import { HallDepositStore } from "@/stores";
import ErrorsFormatter from "@/ui/_common_/errors-formatter";
import MoneyFormatter from "@/ui/_common_/money-formatter";
import { CurrencyUtilities } from "@/utilities";

type TProps = {
  hallDepositStore: HallDepositStore;
};

function HallDepositForm({ hallDepositStore }: TProps) {
  const schema = useHallDepositSchema();
  const resolver = yupResolver(schema);
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm<THallDepositSchema>({
    resolver,
  });

  useResetForm(hallDepositStore.depositQuery, reset);

  useEffect(() => {
    hallDepositStore.fetchAgent();
  }, [hallDepositStore]);

  const intl = useIntl();

  return (
    <Spin
      spinning={
        hallDepositStore.depositQuery.isPending ||
        hallDepositStore.agentQuery.isPending
      }
    >
      <Space direction="vertical">
        <ErrorsFormatter
          queries={[hallDepositStore.depositQuery, hallDepositStore.agentQuery]}
        />
        {hallDepositStore.isAvailableBalanceShown && (
          <Alert
            type="info"
            message={intl.formatMessage(
              {
                id: "HFlRzl",
                defaultMessage: "Available balance: {balance}.",
              },
              {
                balance: (
                  <Typography.Link
                    onClick={() =>
                      setValue(
                        "amount",
                        CurrencyUtilities.toMainUnits(
                          hallDepositStore.availableBalance,
                        ),
                      )
                    }
                  >
                    <MoneyFormatter cents={hallDepositStore.availableBalance} />{" "}
                    {hallDepositStore.agent?.currency}
                  </Typography.Link>
                ),
              },
            )}
          />
        )}
        <Form onFinish={handleSubmit(hallDepositStore.handleSubmit)}>
          <Form.Item
            label={intl.formatMessage({
              defaultMessage: "Amount",
            })}
            validateStatus={has(errors, "amount") ? "error" : ""}
            help={errors.amount?.message}
          >
            <Controller
              control={control}
              name="amount"
              defaultValue={undefined}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={intl.formatMessage({
                    defaultMessage: "Amount",
                  })}
                  suffix={hallDepositStore.hallStore.hall.currency}
                  autoFocus
                  type="number"
                />
              )}
            />
          </Form.Item>
          <Row justify="end" gutter={12}>
            <Col>
              <Button onClick={hallDepositStore.modal.close}>
                {intl.formatMessage({ defaultMessage: "Cancel" })}
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                loading={hallDepositStore.depositQuery.isPending}
              >
                {intl.formatMessage({ defaultMessage: "Deposit" })}
              </Button>
            </Col>
          </Row>
        </Form>
      </Space>
    </Spin>
  );
}

export default observer(HallDepositForm);
